/** Incident list with acknowledge/close and the evidence behind each alarm. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Incident } from '../api/types';
import { Notice } from '../components/Notice';
import { formatDateTime } from '../lib/format';
import { useApi } from '../lib/useApi';
import { useAuth } from '../lib/auth';

const RESOLUTIONS = ['fixed', 'false_alarm', 'curtailment', 'no_fault_found', 'deferred', 'other'];

export function IncidentsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [openOnly, setOpenOnly] = useState(true);
  const { data, loading, errorCode, errorParams, reload } = useApi(
    () => api.incidents({ openOnly, limit: 200 }),
    [openOnly],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function act(action: () => Promise<unknown>, id: string) {
    setBusyId(id);
    setActionError(null);
    try {
      await action();
      reload();
    } catch (err) {
      setActionError((err as { code?: string }).code ?? 'server.error');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <h1>{t('incidents.title')}</h1>

      <div className="toolbar">
        <label className="field field--inline">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(event) => setOpenOnly(event.target.checked)}
          />
          <span>{t('incidents.openOnly')}</span>
        </label>
      </div>

      <Notice tone="error" code={errorCode ?? actionError} params={errorParams} />
      {loading && !data ? <p className="muted">{t('app.loading')}</p> : null}
      {data && data.length === 0 ? <p className="muted">{t('incidents.none')}</p> : null}

      <ul className="incident-list">
        {(data ?? []).map((incident) => (
          <li key={incident.id} className={`incident incident--${incident.severity}`}>
            <div className="incident__head">
              <strong>
                {t(`incidents.rules.${incident.rule_code}`, {
                  defaultValue: incident.rule_code,
                  ...incident.message_params,
                })}
              </strong>
              <span className="badge badge--plain">
                {t(`incidents.severities.${incident.severity}`)}
              </span>
              <span className="badge badge--plain">
                {t(`incidents.statuses.${incident.status}`)}
              </span>
            </div>

            <dl className="definition definition--inline">
              <dt>{t('incidents.opened')}</dt>
              <dd>{formatDateTime(incident.opened_at)}</dd>
              {incident.device_name ? (
                <>
                  <dt>{t('incidents.device')}</dt>
                  <dd>{incident.device_name}</dd>
                </>
              ) : null}
              {incident.resolution ? (
                <>
                  <dt>{t('incidents.resolution')}</dt>
                  <dd>
                    {t(`incidents.resolutions.${incident.resolution}`, {
                      defaultValue: incident.resolution,
                    })}
                  </dd>
                </>
              ) : null}
            </dl>

            {incident.rule_code === 'device.zero_production' ? (
              <Notice tone="info">{t('incidents.suspicionNotice')}</Notice>
            ) : null}

            <Evidence incident={incident} />

            {!incident.closed_at ? (
              <div className="incident__actions">
                {can('incidents.ack') && !incident.acknowledged_at ? (
                  <button
                    type="button"
                    className="button button--ghost"
                    disabled={busyId === incident.id}
                    onClick={() => void act(() => api.acknowledgeIncident(incident.id), incident.id)}
                  >
                    {t('incidents.acknowledge')}
                  </button>
                ) : null}
                {can('incidents.close') ? (
                  <CloseForm
                    busy={busyId === incident.id}
                    onClose={(resolution, rootCause) =>
                      void act(
                        () => api.closeIncident(incident.id, resolution, rootCause),
                        incident.id,
                      )
                    }
                  />
                ) : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
}

function Evidence({ incident }: { incident: Incident }) {
  const { t } = useTranslation();
  const entries = Object.entries(incident.evidence ?? {});
  if (entries.length === 0) return null;
  return (
    <details className="evidence">
      <summary>{t('incidents.evidence')}</summary>
      <dl className="definition">
        {entries.map(([key, value]) => (
          <div key={key}>
            <dt>{key}</dt>
            <dd>{String(value)}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function CloseForm({
  busy,
  onClose,
}: {
  busy: boolean;
  onClose: (resolution: string, rootCause?: string) => void;
}) {
  const { t } = useTranslation();
  const [resolution, setResolution] = useState('fixed');
  const [rootCause, setRootCause] = useState('');
  return (
    <form
      className="inline-form"
      onSubmit={(event) => {
        event.preventDefault();
        onClose(resolution, rootCause || undefined);
      }}
    >
      <label className="field field--inline">
        <span className="sr-only">{t('incidents.resolution')}</span>
        <select value={resolution} onChange={(event) => setResolution(event.target.value)}>
          {RESOLUTIONS.map((value) => (
            <option key={value} value={value}>
              {t(`incidents.resolutions.${value}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="field field--inline">
        <span className="sr-only">{t('incidents.rootCause')}</span>
        <input
          value={rootCause}
          placeholder={t('incidents.rootCause')}
          onChange={(event) => setRootCause(event.target.value)}
        />
      </label>
      <button type="submit" className="button button--ghost" disabled={busy}>
        {t('incidents.closeIncident')}
      </button>
    </form>
  );
}
